import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import Field, { FieldProps } from '../Field';
import classes from './style.module.scss';
import useField from '../useField';
import Icon from '~/app/common/Icon';
import Input from '../Search/Input';
import { useStoreActions, useStoreState } from '~/store';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchUser } from '~/types';

export interface UserMultiSelectPropsDefault extends React.DetailedHTMLProps<React.SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement> {
  defaultOptions: SearchUser[],
  optionChange: (selected: SearchUser[]) => void
  position?: 'top' | 'bottom'
}

export type SelectProps = UserMultiSelectPropsDefault & FieldProps

export type UserMultiSelectComponent = React.RefForwardingComponent<HTMLSelectElement, SelectProps>

const UserMultiSelect: UserMultiSelectComponent = (props, ref) => {

  const [selectedOptions, setSelectedOptions] = useState<SearchUser[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const machineId = useStoreState(state => state.machine.data.id);
  const searchUsers = useStoreState(state => state.user.searchUsers);
  const fetchingSearchUsers = useStoreState(state => state.user.fetchingSearchUsers ?? []);
  const searchUserAction = useStoreActions(actions => actions.user.fetchSearchUser ?? []);
  const searchUsersTotal = useStoreState(state => state.user.searchUsersTotal ?? 0);

  const {
    fieldProps: { icon = 'arrow_drop_down', iconPosition = 'right', iconSize = 32, ...fieldProps },
    inputProps: { defaultOptions, optionChange, placeholder, position = 'top'}
  } = useField<UserMultiSelectPropsDefault>(props);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleOptionToggle = useCallback((value: SearchUser) => {
    if (selectedOptions.includes(value)) {
      setSelectedOptions(selectedOptions.filter(v => v !== value));
    } else {
      setSelectedOptions([...selectedOptions, value]);
      setSearchText('');
      setIsOpen(false);
    }
  }, [selectedOptions]);

  const isUserSelected = useCallback((id: string) => {
    return selectedOptions.some(v => v.id === id);
  }, [selectedOptions]);

  const handleTagRemove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, value: SearchUser) => {
    e.stopPropagation();
    handleOptionToggle(value);
  }

  const searchUser = useCallback((searchText: string) => {
    setSearchText(searchText);
  }, []);

  const fetchNext = useCallback(() => {
    if (machineId) searchUserAction({ searchText, offset: searchUsers.length, machineId });
  }, [searchText, searchUserAction, searchUsers.length, machineId])

  useEffect(() => {
    setSelectedOptions(defaultOptions);
  }, [defaultOptions]);

  useEffect(() => {
    optionChange(selectedOptions);
  }, [selectedOptions, optionChange]);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const currentRef = dropdownRef?.current as HTMLElement | null;
      if (currentRef && !currentRef.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside, { passive: true });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (machineId) searchUserAction({ searchText, offset: 0, machineId });
  }, [searchUserAction, searchText, machineId]);

  return (
    <Field
      icon={icon}
      iconPosition={iconPosition}
      iconSize={iconSize}
      {...fieldProps}
    >
      <div ref={dropdownRef} className={classes['custom-dropdown']}>
        <div
          className={classes['custom-dropdown__toggle']}
          onClick={toggleDropdown}
          role="button"
          aria-expanded={isOpen}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') toggleDropdown();
          }}
        >
          <div className={classes['custom-dropdown__tags']}>
            {selectedOptions.length === 0 ? (
              <span>{placeholder}</span>
            ) : (
              selectedOptions.map((user) => {
                return (
                  <span key={user.id} className={classes['custom-dropdown__tag']}>
                    <div className={classes['userListItem']}>
                      <img src={user.avatar} alt={user.name} />
                      {user.name}
                    </div>
                    <button type="button" className={classes['custom-dropdown__tag-remove']} onClick={e => handleTagRemove(e, user)}>
                      <Icon name='close' size={14} />
                    </button>
                  </span>
                );
              })
            )}
          </div>
        </div>

        {isOpen && (
          <div className={`${classes['custom-dropdown__menu']} ${classes[position]}`}>
            <div className={classes['custom-dropdown__search-container']}>
              <Input
                autoCorrect="off"
                placeholder="Search..."
                value={searchText}
                onSearch={searchUser}
                fetching={fetchingSearchUsers}
                className={classes['custom-dropdown__search']}
              />
            </div>

            {searchUsers.length > 0 ? (
              <>
                <InfiniteScroll
                  dataLength={searchUsers.length}
                  next={fetchNext}
                  hasMore={searchUsers.length < searchUsersTotal}
                  loader={<div></div>}
                  scrollableTarget="search-scroll-wrapper"
                >
                  {searchUsers.map((user) => (
                    <div key={user.id}
                      className={`${classes['userItem']} ${isUserSelected(user.id) ? classes['selected'] : ''}`}
                      onClick={() => handleOptionToggle(user)}
                    >
                      <div className={classes['userListItem']}>
                        <img src={user.avatar} alt={user.name} />
                        {user.name}
                      </div>
                    </div>
                  ))}
                </InfiniteScroll>
              </>
            ) : (
              <div className={classes['custom-dropdown__no-results']}>No results found</div>
            )}
          </div>
        )}
      </div>
    </Field>
  );
};

export default memo(React.forwardRef(UserMultiSelect))
